import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FloppyDisk } from "@phosphor-icons/react";

export default function SystemSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data));
  }, []);

  if (!s) return <div className="text-sm text-zinc-500 p-6">Loading settings…</div>;

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const setFeature = (k, v) => setS((prev) => ({ ...prev, features: { ...prev.features, [k]: v } }));

  const total = (s.reward_split_contributor_pct || 0) + (s.reward_split_reviewer_pct || 0) + (s.reward_split_reserve_pct || 0);

  const save = async () => {
    if (total !== 100) {
      toast.error(`Reward split must total 100% (currently ${total}%)`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/settings", s);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="system-settings-page">
      {/* Approvals */}
      <Section title="Approval thresholds" caption="Tasks above these thresholds require manager approval before going Open.">
        <Row label="Effort (hours) triggering approval">
          <Input type="number" min={1} max={200} value={s.approval_effort_hours_threshold} onChange={(e) => set("approval_effort_hours_threshold", Number(e.target.value))} className="rounded-sm max-w-[160px]" data-testid="setting-effort-threshold" />
        </Row>
        <Row label="Credit cost triggering approval">
          <Input type="number" min={1} value={s.approval_credit_cost_threshold} onChange={(e) => set("approval_credit_cost_threshold", Number(e.target.value))} className="rounded-sm max-w-[160px]" data-testid="setting-credit-threshold" />
        </Row>
        <Row label="Priorities triggering approval">
          <div className="flex gap-4">
            {["low", "medium", "high", "critical"].map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={(s.approval_priority_triggers || []).includes(p)}
                  onChange={(e) => {
                    const cur = s.approval_priority_triggers || [];
                    set("approval_priority_triggers", e.target.checked ? [...cur, p] : cur.filter((x) => x !== p));
                  }}
                  data-testid={`priority-trigger-${p}`}
                />
                <span className="uppercase text-xs tracking-wider">{p}</span>
              </label>
            ))}
          </div>
        </Row>
      </Section>

      {/* Self-pick & workload */}
      <Section title="Participation limits">
        <Row label={`Self-pick effort cap: ${s.self_pick_effort_cap}h`} caption="Max effort_hours allowed for self-pick (higher requires application flow).">
          <Slider value={[s.self_pick_effort_cap]} onValueChange={(v) => set("self_pick_effort_cap", v[0])} min={0} max={40} step={1} data-testid="setting-selfpick-slider" />
        </Row>
        <Row label={`Workload cap: ${s.workload_cap_percent}%`} caption="Employees above this workload cannot apply for new tasks.">
          <Slider value={[s.workload_cap_percent]} onValueChange={(v) => set("workload_cap_percent", v[0])} min={50} max={100} step={5} data-testid="setting-workload-slider" />
        </Row>
      </Section>

      {/* Reward distribution */}
      <Section title="Reward distribution split" caption={`Must total 100%. Currently: ${total}%`}>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Contributor %</Label>
            <Input type="number" min={0} max={100} value={s.reward_split_contributor_pct} onChange={(e) => set("reward_split_contributor_pct", Number(e.target.value))} className="rounded-sm" data-testid="setting-contributor-pct" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Reviewer %</Label>
            <Input type="number" min={0} max={100} value={s.reward_split_reviewer_pct} onChange={(e) => set("reward_split_reviewer_pct", Number(e.target.value))} className="rounded-sm" data-testid="setting-reviewer-pct" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Marketplace reserve %</Label>
            <Input type="number" min={0} max={100} value={s.reward_split_reserve_pct} onChange={(e) => set("reward_split_reserve_pct", Number(e.target.value))} className="rounded-sm" data-testid="setting-reserve-pct" />
          </div>
        </div>
        <div className={`mt-2 text-xs ${total === 100 ? "text-emerald-600" : "text-red-600"}`}>Total: {total}%</div>
      </Section>

      {/* Credits rollover */}
      <Section title="Monthly credit rollover" caption="What happens to unspent department credits at month-end.">
        <Row label="Policy">
          <Select value={s.credit_rollover_policy} onValueChange={(v) => set("credit_rollover_policy", v)}>
            <SelectTrigger className="rounded-sm max-w-[220px]" data-testid="setting-rollover-policy"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="reset">Reset (unused expires)</SelectItem>
              <SelectItem value="full">Full rollover (accumulate)</SelectItem>
              <SelectItem value="partial">Partial rollover</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        {s.credit_rollover_policy === "partial" && (
          <Row label={`Carry-over: ${s.credit_rollover_partial_pct}% of unused`}>
            <Slider value={[s.credit_rollover_partial_pct]} onValueChange={(v) => set("credit_rollover_partial_pct", v[0])} min={0} max={100} step={5} data-testid="setting-rollover-partial" />
          </Row>
        )}
      </Section>

      {/* Feature toggles */}
      <Section title="Feature toggles" caption="Turn platform features on/off org-wide.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ["ai_enrichment", "AI task enrichment"],
            ["ai_recommendations", "AI contributor matching"],
            ["ai_executive_summary", "AI executive summary"],
            ["self_pick", "Self-pick (skip application)"],
            ["google_oauth", "Google SSO"],
            ["innovation_challenges", "Innovation challenges (beta)"],
            ["recognition_store", "Recognition store (beta)"],
          ].map(([key, label]) => (
            <div key={key} className="flex items-center justify-between border border-border p-3">
              <div className="text-sm">{label}</div>
              <Switch
                checked={!!s.features?.[key]}
                onCheckedChange={(v) => setFeature(key, v)}
                data-testid={`feature-${key}`}
              />
            </div>
          ))}
        </div>
      </Section>

      <div className="sticky bottom-0 border-t border-border bg-background p-3 flex justify-end">
        <Button onClick={save} disabled={busy} className="rounded-sm bg-foreground text-background" data-testid="save-settings-btn">
          <FloppyDisk size={14} className="mr-1" /> {busy ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, caption, children }) {
  return (
    <div className="border border-border bg-background p-5">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Section</div>
        <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
        {caption && <p className="text-xs text-zinc-500 mt-1">{caption}</p>}
      </div>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

function Row({ label, caption, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold mb-1">{label}</div>
      {caption && <div className="text-xs text-zinc-500 mb-2">{caption}</div>}
      {children}
    </div>
  );
}
